import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import EntityTable from '../components/shared/EntityTable';
import EntityModal from '../components/modals/EntityModal';
import { useAppStore } from '../store';
import { Supplier } from '../types';

const Suppliers: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  
  const { suppliers, addSupplier, updateSupplier } = useAppStore();
  
  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (supplier.email && supplier.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setIsModalOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsModalOpen(true);
  };
  
  const handleSaveSupplier = async (newSupplier: Omit<Supplier, 'id' | 'balance'>) => {
    try {
      if (selectedSupplier) {
        // Update existing supplier
        await updateSupplier(selectedSupplier.id, newSupplier);
      } else {
        // Add new supplier
        await addSupplier(newSupplier);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      throw error;
    }
    setIsModalOpen(false);
    setSelectedSupplier(null);
  };
  
  const handleViewDetails = (id: string) => {
    // This function is handled in EntityTable
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar proveedores..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        <Button 
          variant="primary" 
          icon={<Plus className="h-5 w-5" />}
          onClick={handleAddSupplier}
        >
          Nuevo Proveedor
        </Button>
      </div>
      
      <Card>
        <CardHeader title={`Proveedores (${filteredSuppliers.length})`} />
        
        <EntityTable
          data={filteredSuppliers}
          entityType="supplier"
          onViewDetails={handleViewDetails}
          onEdit={handleEditSupplier}
        />
      </Card>

      <EntityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entityType="supplier"
        onSave={handleSaveSupplier}
        initialData={selectedSupplier || undefined}
      />
    </div>
  );
};

export default Suppliers;